export const getAppVersion = (): string => {
  if (typeof __APP_VERSION__ !== "undefined") {
    return __APP_VERSION__;
  }
  return process.env["APP_VERSION"] ?? "unknown";
};

export const isPreviewVersion = (version: string): boolean => {
  return /^[0-9a-f]{6}$/i.test(version);
};

// Skew only depends on major/minor; a regex avoids shipping `semver` in the front entry.
// Grammar from semver.org: numeric prerelease identifiers have no leading zeroes, identifiers are never empty.
const NUMERIC_IDENTIFIER = String.raw`0|[1-9]\d*`;
const PRERELEASE_IDENTIFIER = String.raw`(?:${NUMERIC_IDENTIFIER}|\d*[A-Za-z-][0-9A-Za-z-]*)`;
const BUILD_IDENTIFIER = "[0-9A-Za-z-]+";
const SEMVER_REGEX = new RegExp(
  String.raw`^v?(${NUMERIC_IDENTIFIER})\.(${NUMERIC_IDENTIFIER})\.(${NUMERIC_IDENTIFIER})` +
    String.raw`(?:-${PRERELEASE_IDENTIFIER}(?:\.${PRERELEASE_IDENTIFIER})*)?` +
    String.raw`(?:\+${BUILD_IDENTIFIER}(?:\.${BUILD_IDENTIFIER})*)?$`,
);

interface Release {
  major: number;
  minor: number;
  patch: number;
}

const parseRelease = (version: string): Release | null => {
  const match = SEMVER_REGEX.exec(version);
  if (!match) {
    return null;
  }
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
};

const compareMajorMinor = (backend: Release, frontend: Release): VersionSkew => {
  if (backend.major === frontend.major && backend.minor === frontend.minor) {
    return "compatible";
  }

  const backendIsAhead =
    backend.major > frontend.major || (backend.major === frontend.major && backend.minor > frontend.minor);
  return backendIsAhead ? "front-behind" : "front-ahead";
};

/** `front-ahead` happens mid-rollout, when the backend still answers with the previous version. */
export type VersionSkew = "compatible" | "front-behind" | "front-ahead" | "unknown";

export const getVersionSkew = (backendVersion: string | null, frontendVersion: string): VersionSkew => {
  if (!backendVersion) {
    return "unknown";
  }

  const backendIsPreview = isPreviewVersion(backendVersion);
  const frontendIsPreview = isPreviewVersion(frontendVersion);

  if (backendIsPreview && frontendIsPreview) {
    return backendVersion === frontendVersion ? "compatible" : "unknown";
  }

  const backend = parseRelease(backendVersion);
  const frontend = parseRelease(frontendVersion);
  if (!backend || !frontend) {
    return "unknown";
  }

  return compareMajorMinor(backend, frontend);
};

/** Unlike `getVersionSkew`, patch releases count. */
export const isBackendReleaseNewer = (backendVersion: string | null, frontendVersion: string): boolean => {
  const backend = backendVersion ? parseRelease(backendVersion) : null;
  const frontend = parseRelease(frontendVersion);
  if (!backend || !frontend) {
    return false;
  }

  if (backend.major !== frontend.major) {
    return backend.major > frontend.major;
  }
  if (backend.minor !== frontend.minor) {
    return backend.minor > frontend.minor;
  }
  return backend.patch > frontend.patch;
};
